import { normalizeRichTextStorage } from "../lib/richTextEditor";

export const DEFAULT_APPLICANT_INVITE_SUBJECT = "Continue your application for {{JobTitle}}";

export const APPLICANT_INVITE_PLACEHOLDER_APPLICANT_NAME = "{{ApplicantName}}";
export const APPLICANT_INVITE_PLACEHOLDER_JOB_TITLE = "{{JobTitle}}";
export const APPLICANT_INVITE_PLACEHOLDER_APPLICATION_LINK = "{{ApplicationLink}}";
export const APPLICANT_INVITE_PLACEHOLDER_HIRING_MANAGER_NAME = "{{HiringManagerName}}";
export const APPLICANT_INVITE_PLACEHOLDER_COMPANY_NAME = "{{CompanyName}}";

export const INVITE_EMAIL_PLACEHOLDER_TOKENS = [
  APPLICANT_INVITE_PLACEHOLDER_APPLICANT_NAME,
  APPLICANT_INVITE_PLACEHOLDER_JOB_TITLE,
  APPLICANT_INVITE_PLACEHOLDER_APPLICATION_LINK,
  APPLICANT_INVITE_PLACEHOLDER_HIRING_MANAGER_NAME,
  APPLICANT_INVITE_PLACEHOLDER_COMPANY_NAME,
];

export const INVITE_EMAIL_TOKEN_CLASS = "hiring-role-invite-email-token";

/** Wrap invite placeholders in preview styling for the compose UI (not for sent email). */
export function highlightInvitePlaceholderTokensInHtml(html) {
  let out = String(html || "");
  INVITE_EMAIL_PLACEHOLDER_TOKENS.forEach((token) => {
    const wrapped = `<span class="${INVITE_EMAIL_TOKEN_CLASS}">${token}</span>`;
    if (out.includes(wrapped)) return;
    out = out.split(token).join(wrapped);
  });
  return out;
}

/** Remove preview-only span wrappers before persisting or sending. */
export function stripInviteTokenHighlightSpans(html) {
  let out = String(html || "");
  INVITE_EMAIL_PLACEHOLDER_TOKENS.forEach((token) => {
    const wrapped = `<span class="${INVITE_EMAIL_TOKEN_CLASS}">${token}</span>`;
    out = out.split(wrapped).join(token);
  });
  return out;
}

export const APPLICANT_INVITE_LINK_INTRO = "To access the link, please click the link below:";

export const APPLICANT_INVITE_CTA_LABEL = "Continue your application";

/** Branded CTA button HTML — inserted in the closing block before the apply link. */
export function buildApplicantInviteCtaButtonHtml(
  linkHref = APPLICANT_INVITE_PLACEHOLDER_APPLICATION_LINK
) {
  const href = String(linkHref || APPLICANT_INVITE_PLACEHOLDER_APPLICATION_LINK).trim();
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:16px 0 12px;">
  <tr>
    <td align="center" style="padding:20px 16px;background-color:#f8efe3;border:1px solid #e8c49a;border-radius:12px;">
      <a href="${href}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:14px 32px;background-color:#f37021;color:#ffffff;font-weight:700;font-size:15px;line-height:1.2;text-decoration:none;border-radius:10px;font-family:Montserrat,ui-sans-serif,system-ui,sans-serif;">${APPLICANT_INVITE_CTA_LABEL}</a>
    </td>
  </tr>
</table>`;
}

/** Editable middle of the invite email only — fixed opening/closing blocks wrap this in the UI and sent email. */
export const DEFAULT_APPLICANT_INVITE_MESSAGE_HTML = [
  `<p>Thank you for applying for the role of ${APPLICANT_INVITE_PLACEHOLDER_JOB_TITLE}.</p>`,
  "<p>We have received a large number of applications for this position. Like many employers today, we are finding it increasingly difficult to identify genuine candidates due to the growing use of AI generated applications and automated application tools.</p>",
  "<p>To help us better understand the people behind the applications, we are using JOBUWIN as part of our selection process.</p>",
  "<p>We would like to invite you to continue your application through JOBUWIN. As part of the process, you will be asked to submit a short video explaining why you believe you are a good fit for the role.</p>",
  "<p>This is your opportunity to tell us, in your own words, why you are interested in the position and what you feel you can bring to the team. We believe this gives genuine applicants a much better chance to stand out than a CV alone.</p>",
].join("");

export function defaultInviteMessageHtmlForEditor() {
  return highlightInvitePlaceholderTokensInHtml(DEFAULT_APPLICANT_INVITE_MESSAGE_HTML);
}

/** Fixed copy after the editable message and before the apply link. */
export const DEFAULT_APPLICANT_INVITE_FIXED_CLOSING_HTML = [
  "<p>We appreciate the additional effort this requires, but we believe it will help us make a fairer and more informed hiring decision.</p>",
  "<p>Thank you again for your interest in the role.</p>",
].join("");

export const APPLICANT_INVITE_DISCLAIMER =
  "If you didn't apply for this role, you can ignore this email.";

export function buildApplicantInviteSignatureLineHtml() {
  return `<p><strong>${APPLICANT_INVITE_PLACEHOLDER_HIRING_MANAGER_NAME} &amp; the team at ${APPLICANT_INVITE_PLACEHOLDER_COMPANY_NAME}</strong></p>`;
}

/** Full HTML sent to the API. */
export function buildApplicantInviteBodyHtml(messageHtml) {
  const body = normalizeRichTextStorage(messageHtml);
  return [
    `<p><strong>Hi ${APPLICANT_INVITE_PLACEHOLDER_APPLICANT_NAME},</strong></p>`,
    body,
    DEFAULT_APPLICANT_INVITE_FIXED_CLOSING_HTML,
    `<p>${APPLICANT_INVITE_LINK_INTRO}</p>`,
    buildApplicantInviteCtaButtonHtml(),
    `<p><a href="${APPLICANT_INVITE_PLACEHOLDER_APPLICATION_LINK}">${APPLICANT_INVITE_PLACEHOLDER_APPLICATION_LINK}</a></p>`,
    "<p><strong>Kind regards,</strong></p>",
    buildApplicantInviteSignatureLineHtml(),
  ].join("");
}

export const DEFAULT_APPLICANT_INVITE_BODY_HTML = buildApplicantInviteBodyHtml(
  DEFAULT_APPLICANT_INVITE_MESSAGE_HTML
);

export const APPLICANT_INVITE_CSV_TEMPLATE = "name,email\n";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidInviteEmail(email) {
  const value = String(email || "").trim();
  if (!value || value.length > 254) return false;
  return EMAIL_PATTERN.test(value);
}

export function salutationFromFullName(fullName) {
  const trimmed = String(fullName || "").trim();
  if (!trimmed) return "there";
  const first = trimmed.split(/\s+/)[0];
  return first || trimmed;
}

/**
 * Minimal CSV parse for name,email template (handles quoted fields).
 * @param {string} text
 */
export function parseApplicantInviteCsv(text) {
  const lines = String(text || "")
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return { rows: [], errors: ["empty_file"] };
  }

  const header = lines[0].split(",").map((cell) => cell.trim().toLowerCase());
  const nameIdx = header.indexOf("name");
  const emailIdx = header.indexOf("email");
  if (nameIdx === -1 || emailIdx === -1) {
    return { rows: [], errors: ["bad_header"] };
  }

  const rows = [];
  const errors = [];
  const seen = new Set();

  for (let i = 1; i < lines.length; i += 1) {
    const cells = splitCsvLine(lines[i]);
    const name = String(cells[nameIdx] || "").trim();
    const email = String(cells[emailIdx] || "").trim().toLowerCase();
    const lineNumber = i + 1;

    if (!name && !email) continue;

    const rowErrors = [];
    if (!name) rowErrors.push("missing_name");
    if (!email) rowErrors.push("missing_email");
    else if (!isValidInviteEmail(email)) rowErrors.push("invalid_email");
    else if (seen.has(email)) rowErrors.push("duplicate");
    else seen.add(email);

    rows.push({ name, email, lineNumber, errors: rowErrors });
    if (rowErrors.length) {
      errors.push({ lineNumber, email, reasons: rowErrors });
    }
  }

  return { rows, errors };
}

function splitCsvLine(line) {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === "," && !inQuotes) {
      cells.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  cells.push(current);
  return cells;
}

export function validRecipientsFromCsvRows(rows) {
  return rows.filter((row) => row.name && row.email && isValidInviteEmail(row.email) && row.errors.length === 0);
}

export function downloadApplicantInviteCsvTemplate() {
  const blob = new Blob([APPLICANT_INVITE_CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "jobuwin-applicant-invite-template.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
